import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form4562 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 4562: Depreciation & Amortization Guide | TaxZerone"
          description="Learn how to file Form 4562 for depreciation and amortization. TaxZerone simplifies the process with accurate e-filing for your 990-T return."
          keywords=""
          canonical="resources/tax-exempt/form-4562-depreciation-amortization-guide/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 4562{' '}
          <span className="highlight-container">
            <span className="highlight">Depreciation and Amortization </span>
          </span>
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-4562-depreciation-amortization-guide/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 4562 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 4562
                        </strong>{' '}
                        is used to claim{' '}
                        <strong style={{ fontWeight: 600 }}>
                          depreciation and amortization deductions
                        </strong>
                        , make the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Section 179 expense election,
                        </strong>{' '}
                        and report the business or investment use of{' '}
                        <strong style={{ fontWeight: 600 }}>
                          automobiles and other listed property.
                        </strong>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Taxpayers generally file{' '}
                        <strong style={{ fontWeight: 600 }}> Form 4562 </strong>{' '}
                        when claiming depreciation for property placed in
                        service during the tax year, a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Section 179 deduction,
                        </strong>{' '}
                        depreciation on listed property, or amortization that
                        begins during the tax year.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Form 4562 reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          depreciation, amortization, Section 179 deductions,
                          MACRS depreciation, listed property,{' '}
                        </strong>
                        and applicable basis and recovery-period information.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Supported Tax Returns:{' '}
                        </strong>{' '}
                        Form 4562 may be filed with various{' '}
                        <strong style={{ fontWeight: 600 }}>
                          business and tax returns,
                        </strong>{' '}
                        including{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-T
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/business-tax/efile-irs-form-1065-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 1065
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/business-tax/efile-irs-form-1120-s/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 1120-S
                        </Link>{' '}
                        and{' '}
                        <strong style={{ fontWeight: 600 }}> Form 1120</strong>,
                        depending on the taxpayer and the applicable
                        depreciation or amortization requirements.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatnew" sx={{ color: '#0202ff' }}>
                          What’s New?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 4562?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 4562?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to file Form 4562?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatnew"
                >
                  What’s New?
                </Typography>
                <Typography variant="body1">
                  For the 2025 tax year, the IRS has updated{' '}
                  <span style={{ fontWeight: 600 }}> Form 4562</span> with
                  several important changes related to depreciation reporting,
                  Section 179 deductions, and bonus depreciation rules.
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      New lines 19h and 20e were added
                    </span>{' '}
                    to report 50-year property depreciation under General
                    Depreciation System (GDS) and Alternative Depreciation
                    System (ADS) methods.
                  </li>
                  <li>
                    Line 23 is now split into lines 23a and 23b for{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      reporting Section 263A
                    </span>{' '}
                    capitalized costs.{' '}
                  </li>
                  <li>
                    A{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      new line 24c was added{' '}
                    </span>
                    for taxpayers claiming depreciation on aircraft.{' '}
                  </li>
                  <li>
                    The Section 179 deduction{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      limit increased to $2,500,000,
                    </span>
                    with a <span style={{ fontWeight: 600 }}>
                      {' '}
                      $4,000,000
                    </span>{' '}
                    phase-out threshold. The SUV deduction
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      limit is now $31,300
                    </span>
                    .{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      The One Big Beautiful Bill Act
                    </span>{' '}
                    reinstated 100% bonus depreciation for certain qualified
                    property acquired after January 19, 2025.
                  </li>
                  <li>
                    New rules also apply to qualified production property,{' '}
                    <span style={{ fontWeight: 600 }}>
                      solar and wind energy property,
                    </span>{' '}
                    and{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      domestic and foreign research expenditures
                    </span>
                    .
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatis"
                >
                  What is Form 4562?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Form 4562</span>
                  is an IRS tax form used to claim depreciation and amortization
                  deductions for business assets and certain intangible
                  property. Businesses and tax-exempt organizations use this
                  form to report depreciation on assets such as{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    buildings, machinery, vehicles, equipment, computers,
                  </span>{' '}
                  and <span style={{ fontWeight: 600 }}> other property</span>
                  used in business activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The form is also used to amortize intangible assets, including
                  goodwill, patents, copyrights, start-up costs, and
                  research-related expenses. In addition, Form 4562 allows
                  taxpayers to claim Section 179 expense deductions, bonus
                  depreciation, and depreciation deductions calculated under the
                  Modified Accelerated Cost Recovery System ({' '}
                  <span style={{ fontWeight: 600 }}>MACRS</span>).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  By filing 4562 form, taxpayers can determine allowable
                  deductions for qualifying property and reduce their overall
                  taxable income.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<NoteAltOutlinedIcon sx={{ fontSize: 32 }} />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> The Section
                  179D deduction for energy-efficient commercial buildings is
                  claimed separately, generally on Form 7205. Do not use Form
                  4562 to report this deduction. Refer to Form 7205 and its
                  instructions for reporting requirements.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Form 4562?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 4562 is used to report depreciation and amortization
                  deductions for assets used in a business or to produce income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You may need to file Form 4562 if you:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>Own a business or are self-employed</li>
                  <li>Own rental property</li>
                  <li>Operate a corporation or partnership </li>
                  <li>
                    Represent a trust, estate, or certain tax-exempt
                    organization{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The form is used to report depreciation on assets such as
                  machinery, equipment, buildings, vehicles, computers, and
                  rental property. It is also required for taxpayers claiming
                  Section 179 deductions, bonus depreciation, or amortization of
                  qualifying intangible assets and business-related costs.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organizations filing the{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-t-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    990-T form
                  </Link>{' '}
                  may need to file Form 4562 to report depreciation expenses
                  connected to unrelated business income activities.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Complete and file Form 4562 if you are claiming:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    Depreciation for property placed in service during the 2025
                    tax year
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    A Section 179 expense deduction, including any carryover
                    from prior years
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Depreciation for vehicles or other listed property
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Vehicle expense deductions reported on forms other than
                    Schedule C (Form 1040)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Depreciation on a corporate income tax return, other than
                    <Link
                      href="/business-tax/efile-irs-form-1120-s/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 1120-S
                    </Link>
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Amortization of costs that begin during the 2025 tax year
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Generally, a separate 4562 form should be filed for each
                  business or activity that requires depreciation or
                  amortization reporting.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0, lineHeight: 1.2 }}
                      >
                        Ready to attach Form 4562 along with your 990-T return
                        with TaxZerone?
                      </Typography>
                      <Typography variant="body2">
                        Make the e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 130, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form4562`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                  <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="howto"
                >
                  How to file Form 4562?
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4562-depreciation-img.webp" />
                  <source srcSet="/assets/images/f4562-depreciation-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-depreciation-img.png"
                    loading="lazy"
                    alt="Schedule A Part II Line1: Simplified Net Investment Income Calculation "
                    width={760}
                    height={118}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 4562{' '}
                  <span style={{ fontStyle: 'italic' }}>
                    {' '}
                    “Depreciation and Amortization (Including Information on
                    Listed Property)”
                  </span>{' '}
                  and is attached to your tax return to report depreciation,
                  amortization, Section 179 deductions, and business-use
                  property information. Taxpayers must enter the following basic
                  details at the top of the form:
                </Typography>

                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Name(s) shown on return
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name exactly as it appears on the related tax
                  return. This may be an individual name, business name,
                  corporation, partnership, trust, or organization name.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Business or activity to which this form relates
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide the specific business activity or operation connected
                  to the depreciation or amortization being claimed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Identifying number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the taxpayer identification number associated with the
                  return, such as a Social Security Number (SSN), Employer
                  Identification Number (EIN), or Individual Taxpayer
                  Identification Number (ITIN).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I-Election to Expense Certain Property Under Section 179
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4562-part1-img.webp" />
                  <source srcSet="/assets/images/f4562-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-part1-img.png"
                    loading="lazy"
                    alt="Schedule A Part II Line1: Simplified Net Investment Income Calculation "
                    width={760}
                    height={306}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1 - Maximum Amount
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    For tax year 2025, the maximum Section 179 expense deduction
                    is <span style={{ fontWeight: 600 }}> $2,500,000</span> for
                    eligible Section 179 property, including qualified real
                    property placed in service during the year.
                  </li>
                  <li>
                    Taxpayers may use Worksheet 1 in the IRS instructions to
                    calculate the allowable deduction amount. If the property is
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      not used more than 50%
                    </span>{' '}
                    for business purposes before the end of its recovery period,
                    the previously claimed Section 179 deduction may need to be
                    recaptured and reported as other income.
                  </li>
                  <li>
                    Additional recapture rules may apply to certain disaster
                    assistance property and other specially designated property
                    if it no longer meets the required usage or location
                    conditions
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2 - Total Cost
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total cost of all Section 179 property placed in
                  service during the 2025 tax year, including qualified real
                  property elected to be treated as Section 179 property.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Listed property reported in Part V</li>
                  <li>
                    Property placed in service by your spouse, even if filing
                    separately
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3 - Threshold Cost
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The Section 179 deduction begins to phase out if the total
                  cost of qualifying Section 179 property placed in service
                  during 2025{' '}
                  <span style={{ fontWeight: 600 }}> exceeds $4,000,000.</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For partnerships and S corporations, these limits apply both
                  at the entity level and the individual partner or shareholder
                  level. Controlled group members are treated as a single
                  taxpayer when applying these limitations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4 - Reduction Limit
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Line 3 from Line 2 to calculate the reduction in the
                  Section 179 deduction limit. If the result is zero or less,
                  enter “0.”
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5 – Dollar Limitation
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the remaining Section 179 deduction available after
                    applying any phase-out reduction on this line.
                  </li>
                  <li>
                    If the amount on Line 5 is zero, no Section 179 deduction
                    can be claimed for the current tax year. In that case, skip
                    Lines 6 through 11, enter zero on Line 12, and report any
                    allowable carryover deduction from 2024 on Line 13.
                  </li>
                  <li>
                    Taxpayers filing separately as married individuals must
                    allocate the Section 179 deduction limit between spouses,
                    either equally or based on an agreed percentage allocation
                    totaling 100%.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6 - Business Income Limitation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Section 179 expense deduction elected for qualifying
                  business property. Do not include listed property on this
                  line. Listed property, such as certain vehicles and other
                  assets reported in Part V, must be entered separately on Line
                  26, column (i).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (a) – Description of Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter a brief description of the property for which the
                  Section 179 deduction is being claimed.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mb: 4,
                    fontStyle: 'italic',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>For example:</strong>{' '}
                  Office furniture, equipment, computers, trucks, machinery,
                  roofs, or qualified improvement property.
                </Alert>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (b) – Cost
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the business-use cost of the property. If the asset was
                  acquired through a trade-in, include only the excess amount
                  paid over the value of the traded property. Do not include any
                  carryover basis from the exchanged asset.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (c) – Elected Cost
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the amount of the property cost you choose to expense
                  under Section 179. Any remaining amount not claimed under
                  Section 179 may still qualify for depreciation using MACRS or
                  other applicable depreciation methods.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you are reporting a Section 179 deduction received from a
                  partnership or S corporation, enter “From{' '}
                  <Link
                    href="/resources/business-tax/schedule-k1-1065/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule K-1 ( Form 1065 )
                  </Link>
                  ” or “
                  <Link
                    href="/resources/business-tax/schedule-k-1/"
                    sx={{ color: '#0202ff' }}
                  >
                    From Schedule K-1 (Form 1120-S)
                  </Link>{' '}
                  ” across columns (a) and (b).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 7 - Current Year Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount elected for listed property from Line 29.
                  Listed property generally includes certain vehicles and other
                  assets used for both personal and business purposes.
                  Additional details for listed property reporting can be found
                  in Part V of Form 4562.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 8 - Total Elected Expense Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts reported in{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    column (c) of Lines 6
                  </span>{' '}
                  and <span style={{ fontWeight: 600 }}> Line 7</span>. This
                  total represents the combined Section 179 expense deduction
                  elected for all qualifying property during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 9 - Tentative Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller amount between{' '}
                  <span style={{ fontWeight: 600 }}> Line 5</span> and{' '}
                  <span style={{ fontWeight: 600 }}> Line 8</span>. This amount
                  represents the tentative deduction before applying business
                  income limitations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 10 - Carryover From Prior Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter any Section 179 deduction{' '}
                  <span style={{ fontWeight: 600 }}> carryover from 2024 </span>{' '}
                  that was not allowed because of the business income
                  limitation. If you filed Form 4562 for 2024, use the amount{' '}
                  <span style={{ fontWeight: 600 }}> reported on Line 13</span>{' '}
                  of that return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This carryover allows taxpayers to claim previously disallowed
                  Section 179 deductions in future tax years if sufficient
                  business income is available.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 11 - Business Income Limitation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The Section 179 deduction cannot exceed taxable income earned
                  from the active conduct of a trade or business during the tax
                  year. Taxpayers are considered to actively conduct a business
                  only if they meaningfully participate in its operations or
                  management. Passive investors generally do not qualify.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  The calculation for this limitation varies depending on the
                  type of taxpayer:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Individuals</span>{' '}
                    generally, the limitation is based on taxable income earned
                    from actively participating in a trade or business,
                    including certain wages and compensation received as an
                    employee.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Partnerships</span> must
                    use the partnership’s active trade or business income and
                    expense items under Section 702(a).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> S corporations</span>{' '}
                    must calculate the limitation based on active business
                    income reported under Section 1366(a).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Corporations</span> other
                    than S corporations must use taxable income before applying
                    Section 179 deductions, net operating losses, and special
                    deductions.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The allowable deduction is the smaller of the business income
                  limitation or the amount on Line 5.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 12 - Allocated Section 179 Deduction
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The limits apply to the taxpayer as a whole, rather than
                    separately to each business activity. Taxpayers with
                    multiple businesses may allocate the allowable Section 179
                    deduction among different activities.
                  </li>
                  <li>
                    To do this, prepare a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      separate Form 4562 marked “Summary” in Part I
                    </span>{' '}
                    to report the combined totals from all businesses or
                    activities. Then, enter the allocated deduction amount on
                    Line 12 of each separate Form 4562 prepared for individual
                    businesses or activities
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 13 - Section 179 Carryover to 2026
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To calculate the deduction carryover to 2026, add the amounts
                  from Lines 9 and 10, then subtract the amount on Line 12.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<NoteAltOutlinedIcon sx={{ fontSize: 32 }} />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Do not use
                  Part II or Part III for listed property. Depreciation and
                  Section 179 deductions for listed property must be reported
                  separately in Part V of Form 4562.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II-Special Depreciation Allowance and Other Depreciation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4562-part2-img.webp" />
                  <source srcSet="/assets/images/f4562-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-part2-img.png"
                    loading="lazy"
                    alt="Form 4562 Depreciation and Amortization Part II-Special Depreciation Allowance and Other Depreciation Line14-Line16"
                    width={760}
                    height={81}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 14 - Special Depreciation Allowance
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the special depreciation allowance (bonus depreciation)
                  for qualified property placed in service during the tax year.
                  This deduction is claimed after any Section 179 deduction and
                  before calculating regular MACRS depreciation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  For 2025, certain qualified property may be eligible for 100%
                  bonus depreciation. Eligible assets generally include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Tangible business property with a recovery period of 20
                    years or less
                  </li>
                  <li>Qualified computer software </li>
                  <li>Water utility property </li>
                  <li>
                    Certain film, television, theatrical, and sound recording
                    productions{' '}
                  </li>
                  <li>Certain plants bearing fruits and nuts </li>
                  <li>Qualified production property </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Some property acquired before January 20, 2025, may qualify
                  for a reduced 40% or 60% bonus depreciation rate.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Taxpayers can also elect out of bonus depreciation for a
                  specific class of property by attaching a statement to the tax
                  return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 15 - Other Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter depreciation expenses calculated using{' '}
                  <span style={{ fontWeight: 600 }}> non-MACRS methods,</span>{' '}
                  including the unit-of-production method and other approved
                  depreciation methods.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Taxpayers must attach a separate statement describing the
                  property, depreciation method used, and adjusted depreciable
                  basis after applying deductions, credits, and bonus
                  depreciation adjustments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 16 - Depreciation for Special Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report depreciation for property subject to special
                  depreciation rules or older recovery systems. This may
                  include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Property placed in service before 1981</li>
                  <li>ACRS property </li>
                  <li>Certain films, sound recordings, and videotapes </li>
                  <li>Property using the income forecast method </li>
                  <li>Certain intangible assets and computer software </li>
                  <li>Patents, copyrights, and mortgage servicing rights </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The total depreciation claimed cannot exceed the adjusted
                  depreciable basis of the property.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III-MACRS Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Section-A
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4562-part3-img.webp" />
                  <source srcSet="/assets/images/f4562-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-part3-img.png"
                    loading="lazy"
                    alt="Form 4562 Depreciation and Amortization Part III-MACRS Depreciation Section-A Line17-Line18"
                    width={760}
                    height={82}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 17 - MACRS Depreciation for Prior-Year Property{' '}
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Line 17 is used to report current-year depreciation
                    deductions for tangible property placed in service before
                    2025 and depreciated under MACRS.
                  </li>
                  <li>
                    The depreciation deduction is calculated based on the
                    asset’s recovery period, depreciation method, and applicable
                    convention under MACRS rules.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<NoteAltOutlinedIcon sx={{ fontSize: 32 }} />}
                  sx={{ mb: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> If part of
                  a MACRS asset is sold, disposed of, or replaced during the tax
                  year, taxpayers may need to adjust the asset’s basis and
                  depreciation reserve before calculating the current-year
                  deduction. The updated basis is then used to determine
                  depreciation for both the remaining asset and the disposed
                  portion.
                </Alert>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    These rules are especially important when reporting partial
                    asset dispositions or property improvements.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 18 – General Asset Account Election
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Elect a General Asset Account (GAA) under MACRS to group
                  similar business assets and depreciate them as a single
                  account rather than tracking each asset separately.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Assets included in the same account must:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Be placed in service during the same tax year</li>
                  <li>Use the same depreciation method </li>
                  <li>Have the same recovery period and convention </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Assets used for{' '}
                  <span style={{ fontWeight: 600 }}> both personal </span> and
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    business purposes cannot be included{' '}
                  </span>{' '}
                  in a General Asset Account.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  When an asset within the account is sold or disposed of, the
                  overall depreciation basis and reserve of the account
                  generally remain unchanged, and the amount received is usually
                  treated as ordinary income.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Special rules may apply to{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    passenger automobiles, foreign-use property, personal-use
                    conversions,
                  </span>{' '}
                  and like-kind exchanges.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To make the election,{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    check the box on Line 18{' '}
                  </span>{' '}
                  and file the return on time, including extensions. Once made,
                  the election is generally irrevocable.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section B-Assets Placed in Service During 2025 Tax Year Using
                  the General Depreciation System
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f4562-part3-secb-img.webp" />
                  <source srcSet="/assets/images/f4562-part3-secb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-part3-secb-img.png"
                    loading="lazy"
                    alt="Form 4562 Depreciation and Amortization Section B-Assets Placed in Service During 2025 Tax Year Using the General Depreciation System Line19"
                    width={760}
                    height={224}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Section B applies to property acquired through a like-kind
                  exchange or involuntary conversion, such as property replaced
                  after theft, casualty, or condemnation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In most cases, the carryover basis of the acquired property
                  must continue to be depreciated using:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>The same depreciation method </li>
                  <li>The same convention </li>
                  <li>The remaining recovery period of the old property </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If additional money is paid as part of the exchange, that
                  excess basis is treated as new property and depreciated
                  separately.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Taxpayers may also elect out of these rules and instead treat
                  the old property as disposed of at the time of the exchange.
                  In that case, the replacement property is treated as newly
                  placed in service on the acquisition date and depreciated
                  accordingly.
                </Typography>

                <Alert
                  severity="warning"
                  icon={<NoteAltOutlinedIcon sx={{ fontSize: 32 }} />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> To make
                  this election, attach a statement to the tax return indicating
                  an election under section 1.168(i)-6(i). The election must be
                  made with a timely filed return, including extensions, and
                  generally cannot be revoked without IRS consent.
                </Alert>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Lines 19 - MACRS Depreciation Under GDS
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Lines 19a through 19j of Form 4562 are used to report
                  depreciation for assets placed in service during the 2025 tax
                  year under the General Depreciation System (GDS) of MACRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  These lines apply to most business property except automobiles
                  and other listed property reported in Part V.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Each line represents a different property classification based
                  on the asset’s recovery period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (a)- Classification of Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the correct MACRS classification based on the type of
                  property and applicable recovery period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The classification determines the depreciation method and
                  recovery period used to calculate depreciation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (b)- Month and Year Placed in Service
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the month and year the property was placed in service
                  and ready for business use.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If property converted from personal use to business use, enter
                  the conversion date.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (c)- Basis for Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the business or investment-use portion of the property’s
                  cost or adjusted basis.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Reduce the basis by:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Section 179 deductions</li>
                  <li>Bonus depreciation </li>
                  <li>Energy-related deductions and credits </li>
                  <li>Other applicable basis adjustments </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Only the remaining adjusted basis is used for depreciation
                  calculations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (d)- Recovery Period
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the applicable recovery period for the asset based on
                  IRS MACRS classifications.{' '}
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mb: 4,
                    fontStyle: 'italic',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Examples include:</strong>{' '}
                  3 years, 5 years, 7 years, 10 years, 15 years, 20 years,25
                  years, 27.5 years, 39 year, 50 years
                </Alert>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (e)- Convention
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the applicable depreciation convention used for the
                  property. The convention determines how much depreciation is
                  allowed in the first and final year.
                </Typography>
                <Typography variant="body1">
                  The three main conventions are:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, '& li': { mb: 1.2, fontWeight: 600 } }}
                >
                  <li>HY (Half-Year Convention)</li>
                  <li>MQ (Mid-Quarter Convention) </li>
                  <li>MM (Mid-Month Convention) </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Most business property uses the half-year convention unless
                  the mid-quarter rules apply.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (f)- Method
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the depreciation method used for the property.
                </Typography>
                <Typography variant="body1">Common methods include:</Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>200 DB – 200% Declining Balance </li>
                  <li>150 DB – 150% Declining Balance </li>
                  <li>S/L – Straight-Line Method </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The applicable method depends on the property classification
                  and IRS rules.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (g)- Depreciation Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the allowable current-year depreciation deduction for
                  the property.
                </Typography>
                <Typography variant="body1">
                  The deduction is calculated using:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Adjusted depreciable basis </li>
                  <li>Recovery period </li>
                  <li>Convention </li>
                  <li>Depreciation method </li>
                  <li>Applicable IRS depreciation tables or formulas </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If property was sold or disposed of during the year, special
                  depreciation calculation rules may apply.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 19a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report depreciation for assets classified as 3-year property
                  under MACRS.
                </Typography>
                <Typography variant="body1">Examples may include:</Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Certain racehorses </li>
                  <li>Qualified rent-to-own property </li>
                  <li>Certain livestock assets </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These assets are generally depreciated over a 3-year recovery
                  period using the applicable MACRS depreciation method.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 19b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report depreciation for assets classified as 5-year property
                  under MACRS.
                </Typography>
                <Typography variant="body1">
                  Common examples include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Automobiles </li>
                  <li>Computers and office equipment </li>
                  <li>Copiers and calculators </li>
                  <li>Appliances and furniture used in rental activities </li>
                  <li>Certain farming machinery and equipment </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Most 5-year property uses the 200% declining balance method
                  unless another method is elected.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 19c
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report depreciation for assets classified as 7-year property
                  under MACRS.
                </Typography>
                <Typography variant="body1">Examples include:</Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Office furniture</li>
                  <li>Office equipment </li>
                  <li>Railroad track </li>
                  <li>Certain agricultural machinery and fencing </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These assets are generally depreciated over 7 years under
                  MACRS rules.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 19d
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report depreciation for assets classified as 10-year property
                  under MACRS.
                </Typography>
                <Typography variant="body1">This may include: </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Vessels and barges </li>
                  <li>Certain agricultural or horticultural structures </li>
                  <li>Trees and vines bearing fruits or nuts </li>
                  <li>Certain smart electric property </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These assets are depreciated over a 10-year recovery period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 19e
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report depreciation for assets classified as 15-year property
                  under MACRS.
                </Typography>
                <Typography variant="body1">Examples include: </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Qualified improvement property </li>
                  <li>Municipal wastewater treatment plants </li>
                  <li>Telephone distribution property </li>
                  <li>Certain land improvements and utility property </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These assets generally use the 150% declining balance
                  depreciation method.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 19f
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Line 19f is used to report depreciation for 20-year property.
                </Typography>
                <Typography variant="body1">Examples include: </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Farm buildings </li>
                  <li>Certain municipal sewer systems </li>
                  <li>Utility land improvements </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These assets are depreciated over a 20-year recovery period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 19g
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report depreciation for qualifying water utility property with
                  a 25-year recovery period under MACRS.
                </Typography>
                <Typography variant="body1">
                  This includes property used in:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Water collection </li>
                  <li>Water treatment </li>
                  <li>Commercial water distribution systems </li>
                  <li>Certain municipal sewer systems </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These assets are depreciated over 25 years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 19h
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report depreciation for qualifying 50-year property under the
                  General Depreciation System (GDS).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This generally includes railroad grading and tunnel bore
                  improvements used for railroad track construction or
                  improvement.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 19i
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Line 19i is used to report depreciation for residential rental
                  property where at least 80% of the rental income comes from
                  dwelling units.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Residential rental buildings are generally depreciated over
                  27.5 years using the straight-line method and mid-month
                  convention.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 19j
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report depreciation for nonresidential real property, such as
                  office buildings, retail stores, warehouses, and other
                  commercial properties.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These properties are generally depreciated over 39 years using
                  the straight-line method and mid-month convention.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section C-Assets Placed in Service During 2025 Tax Year Using
                  the Alternative Depreciation System
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f4562-part3-secc-img.webp" />
                  <source srcSet="/assets/images/f4562-part3-secc-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-part3-secc-img.png"
                    loading="lazy"
                    alt="Form 4562 Depreciation and Amortization Section C-Assets Placed in Service During 2025 Tax Year Using the Alternative Depreciation System Line20"
                    width={760}
                    height={97}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 20 - MACRS Depreciation Under ADS
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Lines 20a through 20e of Form 4562 are used to report
                  depreciation for assets placed in service during the 2025 tax
                  year and depreciated under the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Alternative Depreciation System (ADS)
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  ADS generally uses the straight-line depreciation method over
                  a longer recovery period compared to GDS.
                </Typography>
                <Typography variant="body1">
                  Property that must generally be depreciated under ADS
                  includes:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Property used predominantly outside the United States{' '}
                  </li>
                  <li>Tax-exempt use property </li>
                  <li>Tax-exempt bond financed property </li>
                  <li>Certain imported property </li>
                  <li>Certain farming business property </li>
                  <li>
                    Property held by an electing real property trade or
                    business{' '}
                  </li>
                  <li>Certain property held by an electing farming business</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Taxpayers may also elect to use ADS instead of GDS for certain
                  property classifications by completing Section C.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 20a
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Report depreciation for assets
                    </span>{' '}
                    placed in service during the 2025 tax year that are
                    depreciated under the Alternative Depreciation System (ADS)
                    and have an established class life.
                  </li>
                  <li>
                    Under ADS, depreciation is generally calculated using the
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      straight-line method
                    </span>{' '}
                    over the asset’s ADS recovery period. Property reported on
                    this line may include business equipment, machinery, or
                    other assets with a defined class life under IRS rules.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 20b
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  This line applies to property depreciated under the
                  Alternative Depreciation System (ADS) that does not have a
                  specific IRS-assigned class life and must be depreciated using
                  the applicable ADS recovery period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 20c
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Line 20c is used to{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      report ADS depreciation for residential rental property.
                    </span>{' '}
                  </li>
                  <li>
                    Residential rental property placed in service after 2017
                    generally uses a 30-year ADS recovery period. This line is
                    commonly used by electing real property trades or businesses
                    required to depreciate rental property under ADS.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 20d
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report ADS depreciation for nonresidential real property, such
                  as office buildings, retail stores, and warehouses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 20e
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report ADS depreciation for water utility property and
                  railroad grading or tunnel bore property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  MACRS Recapture
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  If MACRS property is later sold, exchanged, or disposed of,
                  part of the gain may need to be reported as ordinary income
                  through depreciation recapture.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Recapture may include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Section 179 deductions claimed on the property </li>
                  <li>Special depreciation allowance claimed </li>
                  <li>Certain environmental compliance deductions </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Generally, residential rental property and nonresidential real
                  property are not subject to recapture unless special
                  depreciation allowance was claimed on the property.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV-Summary
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f4562-part4-sum-img.webp" />
                  <source srcSet="/assets/images/f4562-part4-sum-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-part4-sum-img.png"
                    loading="lazy"
                    alt="Form 4562 Depreciation and Amortization Part IV-Summary  Line21-Line23"
                    width={760}
                    height={162}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 21 - Total MACRS and Other Depreciation
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the total depreciation deduction for listed property
                    from line 28.{' '}
                  </li>
                  <li>
                    Listed property generally includes assets such as vehicles,
                    computers, and other property used for both business and
                    personal purposes. The amount entered on this line should
                    match the total calculated in Part V.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 22 - Listed Property and Other Property Total
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Line 22 is used to report the total depreciation and Section
                    179 deduction claimed for the current tax year.{' '}
                  </li>
                  <li>
                    Partnerships and S corporations should not include the
                    Section 179 deduction amount from line 12 on this line.
                    Instead, the deduction is separately passed through to
                    partners or shareholders on{' '}
                    <Link
                      href="/resources/business-tax/schedule-k-1/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Schedule K-1
                    </Link>
                    .
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Lines 23 - Capitalized Costs
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Lines 23a and 23b are used by taxpayers subject to the uniform
                  capitalization rules under Section 263A. These rules require
                  certain direct and indirect costs related to property
                  production or acquisition to be added to the asset’s basis
                  instead of being immediately deducted.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These lines apply to assets reported in Part III that were
                  placed in service during the current tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 23a
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the increase in basis related to interest costs that
                    must be capitalized under Section 263A(f).{' '}
                  </li>
                  <li>
                    This generally applies to businesses that are required to
                    capitalize interest expenses connected to the production of
                    certain property.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 23b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the increase in basis from other capitalized costs
                  required under Section 263A, excluding interest costs reported
                  on line 23a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These costs may include certain production, construction,
                  storage, handling, or indirect business expenses that must be
                  added to the basis of the asset.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part V-Listed Property
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f4562-part5-list-img.webp" />
                  <source srcSet="/assets/images/f4562-part5-list-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-part5-list-img.png"
                    loading="lazy"
                    alt="Form 4562 Depreciation and Amortization Part V-Listed Property Line24 - Line29"
                    width={760}
                    height={370}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Section A is used to provide details about listed property and
                  its business use percentage.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The IRS requires taxpayers to maintain proper records
                  supporting the business use of listed property, especially for
                  vehicles and mixed-use assets.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<NoteAltOutlinedIcon sx={{ fontSize: 32 }} />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> The Section
                  179 expense deduction must be calculated before figuring any
                  special depreciation allowance or regular depreciation
                  deduction. Listed property used 50% or less for qualified
                  business use does not qualify for either the Section 179
                  deduction or special depreciation allowance.
                </Alert>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 24 – Business Use Evidence and Aircraft Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Provide information about the records and documentation
                  supporting the business use of listed property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 24a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Indicate whether you have evidence supporting the business or
                  investment use claimed for the listed property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 24b
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you answered “Yes” on line 24a, line 24b asks whether the
                  supporting evidence is written.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 24c
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Line 24c is used to indicate whether you owned, leased, or
                    chartered an aircraft during the tax year. Check all
                    applicable boxes based on how the aircraft was used.{' '}
                  </li>
                  <li>
                    This information helps determine whether the aircraft
                    qualifies business-use depreciation and other deductions
                    under IRS rules for business aircraft.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 25 - Special Depreciation Allowance
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Line 25 is used to report the{' '}
                    <span style={{ fontWeight: 600 }}>
                      total special depreciation allowance
                    </span>{' '}
                    claimed for qualified listed property placed in service
                    during the tax year.
                  </li>
                  <li>
                    This may include certain qualified property acquired{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      after January 19, 2025,
                    </span>{' '}
                    as well as certain qualified property acquired{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      after September 27, 2017,
                    </span>{' '}
                    and{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      before January 20, 2025
                    </span>
                    .
                  </li>
                  <li>
                    The special depreciation allowance is subject to the overall
                    depreciation and Section 179 deduction{' '}
                    <span style={{ fontWeight: 600 }}>
                      limits applicable to passenger automobiles, trucks, and
                      vans
                    </span>
                    .
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line26 - Depreciation for Listed Property Used More Than 50%
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use Line 26 to report depreciation for listed property used
                  <span style={{ fontWeight: 600 }}> more than 50% </span> for
                  qualified business purposes during the tax year. Property
                  reported on this line may qualify for the Section 179{' '}
                  <span style={{ fontWeight: 600 }}> expense deduction</span>{' '}
                  and{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    special depreciation allowance,
                  </span>{' '}
                  subject to IRS limitations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  A qualified business use generally includes property used
                  directly in your trade or business. However, the following
                  uses are not considered qualified business use:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Investment use, </li>
                  <li>Personal use, </li>
                  <li>
                    Certain related-party or employee use not properly reported
                    as taxable income, and{' '}
                  </li>
                  <li>Non-deductible use under Section 274. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For business aircraft, taxpayers must satisfy both the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    50% business-use test
                  </span>{' '}
                  and the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    25% qualified business-use test
                  </span>{' '}
                  to claim accelerated depreciation or bonus depreciation.
                  Proper records must be maintained to support business travel
                  and usage.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (a) - Type of Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the make, model, or description of each listed property
                  item, such as vehicles or other listed assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (b) - Date Placed in Service
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the date the property was first used for business
                  purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (c) - Business/Investment Use Percentage
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the percentage of business use based on mileage, time
                  used, or other appropriate usage methods.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (d) - Cost or Other Basis
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the original cost or adjusted basis of the property,
                  including sales tax.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (e) - Basis for Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the depreciable business-use basis after reductions for
                  credits, Section 179 deductions, or special depreciation
                  allowances.
                </Typography>

                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (f) - Recovery Period
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the applicable MACRS recovery period based on the
                  property classification.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (g) - Method/Convention
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the depreciation method and convention used, such as 200
                  DB, 150 DB, S/L, HY, MQ, or MM.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (h) - Depreciation Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the allowable depreciation deduction for the current
                  year, subject to passenger automobile limitations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (i) - Elected Section 179 Cost
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount elected under Section 179 for qualifying
                  property used more than 50% in business.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 27 - Depreciation for Listed Property Used 50% or Less
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use Line 27 to report depreciation for listed property used{' '}
                  <span style={{ fontWeight: 600 }}>50% or less</span> in a
                  qualified business use during the tax year. Property reported
                  on this line does not qualify for the Section 179{' '}
                  <span style={{ fontWeight: 600 }}> expense deduction </span>{' '}
                  or{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    special depreciation allowance.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Such property must generally be depreciated using the
                  straight-line method under the Alternative Depreciation System
                  (ADS).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Columns (a) through (h)
                  </span>{' '}
                  are completed in the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    same manner as Line 26
                  </span>{' '}
                  . However, the following special rules apply:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Property used 50% or less for qualified business purposes
                    must be depreciated using the ADS straight-line method.
                  </li>
                  <li>
                    The depreciation deduction is generally calculated by
                    dividing the depreciable basis by the ADS recovery period.
                  </li>
                  <li>
                    Passenger automobile depreciation limits continue to
                    apply.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 28 - Total Listed Property Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the depreciation amounts reported in{' '}
                  <span style={{ fontWeight: 600 }}>column (h)</span> for{' '}
                  <span style={{ fontWeight: 600 }}> lines 25 through 27</span>.
                  Enter the total on this line and also report it on{' '}
                  <span style={{ fontWeight: 600 }}> Line 21</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 29 - Total Deduction for Listed Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the Section 179 expense amounts reported in{' '}
                  <span style={{ fontWeight: 600 }}> column (i)</span> on
                  <span style={{ fontWeight: 600 }}> Line 26</span> . Enter the
                  total on this line and also report it on
                  <span style={{ fontWeight: 600 }}> Line 7</span>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section B-Information on Use of Vehicles
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f4562-part5-secb-img.webp" />
                  <source srcSet="/assets/images/f4562-part5-secb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-part5-secb-img.png"
                    loading="lazy"
                    alt="Form 4562 Depreciation and Amortization Part V-Listed Property Section-B Line30 - Line36"
                    width={760}
                    height={257}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Complete Lines 30 through 36 for each vehicle listed in
                  Section A, unless an exception applies. Employees must provide
                  this information to employers for any employer-provided
                  vehicle used during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Complete Lines 30 through 36 for each vehicle listed in
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    columns (a) through (f)
                  </span>
                  . Each column represents a separate vehicle.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Exception:</span> Employers
                  do not need to complete Lines 30-36 for vehicles used by
                  employees who are not more than 5% owners or related persons
                  if any question on Lines 37 through 41 is answered “Yes.”
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 30 - Total Business/Investment Miles
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      total number of miles
                    </span>{' '}
                    driven during the year{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      for business or investment
                    </span>{' '}
                    purposes.{' '}
                  </li>
                  <li>Do not include commuting miles.</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 31 - Total Commuting Miles
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total miles driven between home and work
                  </span>{' '}
                  during the tax year. These miles are considered commuting
                  miles and are not treated as business use.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 32 - Total Personal Noncommuting Miles
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total personal miles{' '}
                  </span>{' '}
                  driven during the year that are not commuting miles.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 33 - Total Miles Driven During the Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total miles driven during the year
                  </span>{' '}
                  for all purposes by{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    adding Line 30 through Line 32
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 34 - Vehicle Available for Personal Use
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Indicate whether the vehicle was available for personal use
                  during off-duty hours.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 35 - Vehicle Used by 5% Owner or Related Person
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check whether the vehicle was primarily used by a person who
                  owns <span style={{ fontWeight: 600 }}> more than 5%</span> of
                  the business or by a related person.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 36 - Availability of Another Personal Vehicle
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Indicate whether another vehicle was available for personal
                  use during the tax year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section C-Questions for Employers Who Provide Vehicles for Use
                  by Their Employees
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f4562-part5-secc-img.webp" />
                  <source srcSet="/assets/images/f4562-part5-secc-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-part5-secc-img.png"
                    loading="lazy"
                    alt="Form 4562 Depreciation and Amortization Part V-Listed Property Section-c Line37 - Line 41"
                    width={760}
                    height={244}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Employers can satisfy the vehicle substantiation requirements
                  under IRC Section 274(d) by maintaining a written policy that
                  either:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Prohibits all personal use of employer-provided vehicles,
                    including commuting, or{' '}
                  </li>
                  <li>Prohibits personal use except for commuting </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  When these policy requirements are properly met, employees
                  generally are not required to maintain separate mileage
                  records for those vehicles.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<NoteAltOutlinedIcon sx={{ fontSize: 32 }} />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Employers
                  must still maintain sufficient evidence to prove that the
                  vehicle use complies with the written policy conditions.
                </Alert>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 37 - Policy Prohibiting Personal Use
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check <span style={{ fontWeight: 600 }}>“Yes”</span> if the
                  employer maintains a written policy that completely prohibits
                  personal use of the vehicle, including commuting.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  The policy must meet all of the following conditions:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    The employer owns or leases the vehicle for business
                    use{' '}
                  </li>
                  <li>
                    The vehicle is normally kept at the employer’s business
                    premises when not in use{' '}
                  </li>
                  <li>
                    Employees using the vehicle do not live at the business
                    location{' '}
                  </li>
                  <li>
                    Personal use is prohibited except for minimal incidental
                    use{' '}
                  </li>
                  <li>
                    The employer reasonably believes employees are not using the
                    vehicle personally beyond minimal use.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  LLine 38 - Policy Allowing Commuting Only
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check “Yes” if the employer maintains a written policy that
                  prohibits personal use of the vehicle except for commuting.
                  This rule does not apply if the employee using the vehicle is
                  an officer, director, or 1% or greater owner.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  To qualify, the policy must meet all of the following
                  conditions:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    The employer owns or leases the vehicle for business use
                  </li>
                  <li>
                    The employee is required to commute in the vehicle for valid
                    business reasons{' '}
                  </li>
                  <li>
                    Personal use other than commuting or minimal incidental use
                    is prohibited{' '}
                  </li>
                  <li>
                    The employer reasonably believes the vehicle is not used
                    personally beyond commuting{' '}
                  </li>
                  <li>
                    The employer includes the value of commuting use in the
                    employee’s taxable income{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 39 - Employee Vehicle Use Treated as Personal Use
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Indicate whether the employer treats all employee use of
                  company-provided vehicles as personal use for tax reporting
                  purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 40 - Exception for Employers With More Than Five Vehicles
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Employers providing more than five vehicles to employees who
                  are not 5% owners or related persons are not required to
                  complete Section B for those vehicles.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Instead, employers must:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Obtain the required vehicle-use information from employees,
                    and
                  </li>
                  <li>Retain those records for tax documentation purposes </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 41 - Qualified Demonstration Use Vehicles
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check “Yes” if the vehicle qualifies for demonstration use
                  under a written employer policy.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  The policy must:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Restrict vehicle use to full-time automobile
                    salespersons{' '}
                  </li>
                  <li>Prohibit personal vacation use </li>
                  <li>Prohibit storing personal belongings in the vehicle </li>
                  <li>Limit mileage outside normal working hours </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<NoteAltOutlinedIcon sx={{ fontSize: 32 }} />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> If you
                  answered “Yes” to line 37, 38, 39, 40, or 41, you are not
                  required to complete Section B for those vehicles.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part VI-Amortization
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f4562-part6-amortization-img.webp" />
                  <source srcSet="/assets/images/f4562-part6-amortization-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4562-part6-amortization-img.png"
                    loading="lazy"
                    alt="Form 4562 Depreciation and Amortization Part VI-Amortization Line42 - Line44"
                    width={760}
                    height={146}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Part VI is used to claim Amortization deductions for certain
                  capital expenses that are recovered gradually over a fixed
                  number of years instead of being deducted all at once.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Attach any statements or supporting information required under
                  the applicable IRS Code sections and regulations when making
                  an amortization election.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution:</strong> Any
                  amount being amortized cannot also be claimed under the
                  Section 179 expense deduction or regular depreciation.
                </Alert>

                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (a) – Description of Costs
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter a description of the costs being amortized. Common
                  examples include:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Geological and Geophysical Costs – Section 167(h) </li>
                  <li>Pollution Control Facilities – Section 169 </li>
                  <li>Bond Premium – Section 171 </li>
                  <li>
                    Research and Experimental Expenditures – Sections 174 and
                    174A{' '}
                  </li>
                  <li>Lease Acquisition Costs – Section 178 </li>
                  <li>Reforestation Costs – Section 194 </li>
                  <li>
                    Optional Write-Off of Tax Preference Items – Section
                    59(e){' '}
                  </li>
                  <li>Section 197 Intangibles </li>
                  <li>Startup and Organizational Costs </li>
                  <li>Creative Property Costs </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (b) – Date Amortization Begins
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the date the amortization period starts based on the
                    applicable IRS Code section.
                  </li>
                  <li>
                    For pollution control facilities, the amortizable amount
                    must be reduced by any special depreciation allowance
                    claimed on line 14 for that property.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (c) – Amortizable Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount eligible for amortization after
                  applying any required adjustments or limitations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (d) – Code Section
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable IRS Code section authorizing the
                  amortization deduction, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Section 167</li>
                  <li>Section 169 </li>
                  <li>Section 174 </li>
                  <li>Section 197 </li>
                  <li>Section 195 </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  reframe
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (e) - Amortization Period or Percentage
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the applicable amortization period or the percentage
                  used to calculate the amortization deduction for the cost
                  being amortized. The period or percentage depends on the
                  specific IRS Code section that applies to the expense.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Column (f) – Amortization Deduction for the Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Calculate the current-year amortization deduction by using
                  either of the following methods:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Divide the amount in column (c) by the total number of
                    months in the amortization period, then multiply it by the
                    number of months applicable to the 2025 tax year, or{' '}
                  </li>
                  <li>
                    Multiply the amount in column (c) by the applicable
                    percentage shown in column (e).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 42 - Current-Year Amortizable Costs
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Complete Line 42 only for amortizable costs whose amortization
                  period begins during the tax year starting in 2025.
                </Typography>

                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 43 - Prior-Year Amortization Reporting
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you are claiming amortization for costs that began before
                  the 2025 tax year-other than research or experimental
                  expenditures-and you are not otherwise required to file IRS
                  tax form 4562, report the deduction directly on the “Other
                  Deductions” or “Other Expenses” line of your return instead of
                  filing Form 4562.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<NoteAltOutlinedIcon sx={{ fontSize: 32 }} />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong>{' '}
                  Amortization for research or experimental expenditures under
                  former Section 174(b) or amounts elected for 10-year
                  amortization under Section 59(e) must be reported on Line 43
                  of Form 4562.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Under Revenue Procedure 2025-28, taxpayers may elect to deduct
                  the remaining unamortized domestic research expenditures in
                  full or amortize them over a 2-year period beginning after
                  December 31, 2024. Any election made must be reported on Line
                  43 with the required supporting statement.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For foreign research expenditures under Section 174, attach a
                  statement showing the amount being amortized over the required
                  15-year period and report the deduction on Line 43.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 44 - Total Amortization Deduction Reporting
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total amortization deduction for the year, including
                  current-year and prior-year amortization, research expenditure
                  amortization, and allowable reforestation amortization in
                  column(f).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the total on the appropriate{' '}
                  <span style={{ fontWeight: 600 }}> “Other Deductions”</span>{' '}
                  or <span style={{ fontWeight: 600 }}>“Other Expenses”</span>{' '}
                  line of your tax return.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<NoteAltOutlinedIcon sx={{ fontSize: 32 }} />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong>{' '}
                  Partnerships and S corporations must separately report certain
                  amortization items, including reforestation expenses, on{' '}
                  <Link
                    href="/resources/business-tax/schedule-k/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule K
                  </Link>{' '}
                  (Form 1065 and 1120-s) and Schedule K-1 (Form 1065 and
                  1120-s). Refer to the instructions for Forms
                  <Link
                    href="/business-tax/efile-irs-form-1065-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1065
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1120-S
                  </Link>{' '}
                  for additional reporting requirements.
                </Alert>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This content is based on the official instructions for
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          IRS Form 4562
                        </span>{' '}
                        issued by the{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          Internal Revenue Service
                        </span>
                        . For complete guidance, definitions, limitations, and
                        updated rules, refer to the{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          official Form 4562
                        </span>
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i4562.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions
                        </Link>{' '}
                        and related IRS publications.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the difference between depreciation and
                  amortization?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    Depreciation is a method of deducting the{' '}
                    <span style={{ fontWeight: 600 }}>
                      cost of tangible assets
                    </span>{' '}
                    over their useful lives, while amortization is a similar
                    process for{' '}
                    <span style={{ fontWeight: 600 }}>intangible assets</span>.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What is the Modified Accelerated Cost Recovery System
                  (MACRS) on Form 4562?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      <span style={{ fontWeight: 600 }}> MACRS</span> is the
                      most common method of depreciation used on
                      <span style={{ fontWeight: 600 }}> Form 4562</span>. It
                      allows for faster depreciation of property in the early
                      years of its useful life, which can lead to larger
                      deductions and tax savings upfront.
                    </Typography>
                    <Typography variant="body1">
                      Under MACRS, all assets are divided into classes which
                      dictate the number of years over which an asset&apos;s
                      cost will be recovered. Each MACRS class has a
                      predetermined schedule that determines the percentage of
                      the asset&apos;s costs which is depreciated each year.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What types of property are eligible for depreciation or
                  amortization?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    <span style={{ fontWeight: 600 }}> Depreciable</span> assets
                    include{' '}
                    <span style={{ fontWeight: 600 }}>tangible property</span>{' '}
                    used in a business, such as equipment, machinery, vehicles,
                    and buildings.{' '}
                    <span style={{ fontWeight: 600 }}>Amortizable</span> assets
                    include{' '}
                    <span style={{ fontWeight: 600 }}>intangible property</span>
                    , such as patents, copyrights, and goodwill.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. What are Section 179 deductions?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    <span style={{ fontWeight: 600 }}> Section 179</span>{' '}
                    deduction allows businesses to immediately deduct the cost
                    of qualifying business equipment and property in the year it
                    is placed in service, instead of depreciating the asset over
                    several years. This deduction commonly applies to items such
                    as machinery, vehicles, computers, office equipment, and
                    certain business-use property.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. How Can Depreciation Be Stopped or Adjusted?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    You generally cannot cancel depreciation once it is claimed
                    or allowable. However, depreciation stops when the asset is
                    sold, fully depreciated, or no longer used for business or
                    rental purposes.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form4562;
